import {
  Loader,
  getTranslatedText,
  toast,
  useIntl,
  getHeaderValue,
  fetchApi
} from '@nile/components/src';
import { Button, ButtonGroup, Heading, Page } from '@superops/ui-design-system';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import dompurify from 'dompurify';
import {
  Editor,
  mentionsContainerStyle,
  TEXT_LENGTH,
  mentionsItemStyle
} from '@nile/components';
import { cloneDeep, isEmpty } from 'lodash';
import envConfig from '@nile/home/src/Config/env';
import { MentionsInput, Mention } from 'react-mentions';
import { MentionsHolder } from '@nile/client_management/src/Pebbles/EmailTemplate';
import {
  AccountClient,
  GET_EVENT_TEMPLATE_META,
  GET_SCHEDULING_EVENT_TEMPLATE,
  UPDATE_SCHEDULING_EVENT_TEMPLATE,
  useCachedQuery,
  useQuery
} from '@nile/graphql/src';
import { EmailEditor } from '@nile/banyan/src/Components/Editors';
import { useMutation } from '@apollo/client';
import PlaceholderContainer from '@nile/banyan/src/Containers/ListContainers/PlaceholderContainer';
import { dompurifier } from '@nile/components/src/Utils/helpers';

const CustomToolbar = () => {
  return (
    <>
      <button className="ql-link" />
    </>
  );
};

const EventTemplate = (props) => {
  const intl = useIntl();

  const [template, setTemplate] = useState({
    name: '',
    content: '',
    placeholdersUsed: '{}',
    mentions: [],
    attachments: [],
    inlineImages: []
  });

  const [availablePlaceHolders, setAvailablePlaceHolders] = useState([]);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const editorRef = React.useRef(null);

  const updateTemplate = (obj) => {
    const clonedTemplate = cloneDeep(template);
    const updatedTemplate = { ...clonedTemplate, ...obj };
    setTemplate(updatedTemplate);
  };

  const { loading, data } = useCachedQuery(GET_SCHEDULING_EVENT_TEMPLATE, {
    client: AccountClient,
    variables: {
      constant: 'CALENDAR_EVENT_DESCRIPTION'
    },
    onCompleted(res) {
      if (res?.getPlaceholderTemplate) {
        const clonedTemplate = cloneDeep(template);
        const content = res?.getPlaceholderTemplate?.content;
        const placeholders = res?.getPlaceholderTemplate?.placeholdersUsed;
        updateTemplate({
          ...clonedTemplate,
          content: dompurifier(content),
          placeholdersUsed: placeholders
        });
      }
    }
  });

  const { loading: metaLoading, data: metaData } = useCachedQuery(
    GET_EVENT_TEMPLATE_META,
    {
      client: AccountClient,
      fetchPolicy: 'network-only',
      variables: {
        constant: 'CALENDAR_EVENT_DESCRIPTION'
      },
      onCompleted(res) {
        const variables = res?.getPlaceholderTemplateMetaInfo?.variables || [];
        const placeHolderData = [];
        variables.forEach((el) => {
          if (el?.constant === 'WORK_ITEM_FIELDS') {
            const placeHolderFields = el?.fields;
            if (placeHolderFields) {
              placeHolderFields.forEach((field) =>
                placeHolderData.push({
                  key: field,
                  label: getTranslatedText(intl, field),
                  placeholderValue: field,
                  text: getTranslatedText(intl, field),
                  value: getTranslatedText(intl, field),
                  url: field,
                  id: field,
                  display: getTranslatedText(intl, field)
                })
              );
            }
          }
        });

        if (!isEmpty(placeHolderData)) {
          setAvailablePlaceHolders(placeHolderData);
        }
      }
    }
  );

  const handleFileAttachments = (fileInput) => {
    const formData = new FormData();
    const files = [fileInput];
    files.forEach((file, idx) => {
      formData.append(`files`, file);
    });

    const attachmentUploadUrl = `${envConfig.fileRoot}/upload`;
    const uploadParams = {
      // module: 'TICKET',
      // details: JSON.stringify({ operation: 'CREATE' })
      module: 'CALENDAR_EVENT_ATTACHMENT',
      details: JSON.stringify({})
    };

    // if (template?.id) {

    for (const property in uploadParams) {
      formData.append(property, uploadParams[property]);
    }

    setIsFileUploading(true);

    const requestOptions = {
      method: 'POST',
      credentials: 'include',
      body: formData,
      headers: getHeaderValue()
    };
    fetchApi(attachmentUploadUrl, requestOptions)
      .then((response) => {
        return response.json();
      })
      .then((response) => response.data)
      .then((list) => {
        const updatedList = [...list];
        setTemplate({
          ...template,
          attachments: updatedList
        });
        setIsFileUploading(false);
      })
      .catch((error) => {
        setIsFileUploading(false);
      })
      .finally(() => {
        setIsFileUploading(false);
      });
  };

  const handleAttachmentsDeletion = (fileName) => {
    const updatedList = cloneDeep(template?.attachments).filter(
      (item) => item.fileName !== fileName
    );
    setTemplate({ ...template, attachments: updatedList });
  };

  const [updateEventTemplate, { loading: updateLoading }] = useMutation(
    UPDATE_SCHEDULING_EVENT_TEMPLATE,
    {
      client: AccountClient,
      onCompleted() {
        toast('success', 'scheduling.event.template.edit.success');
        if (props?.onClose) {
          props?.onClose();
        }
      }
    }
  );

  const onSave = () => {
    const updatedTemplate = cloneDeep(template);
    const placeHolders = JSON.parse(updatedTemplate?.placeholdersUsed);
    const contentPlaceHolders = placeHolders?.content || {};
    updateEventTemplate({
      variables: {
        input: {
          constant: 'CALENDAR_EVENT_DESCRIPTION',
          content: updatedTemplate?.content,
          placeholdersUsed: JSON.stringify(contentPlaceHolders)
        }
      }
    });
  };

  const onCancel = () => {
    props?.onCancel();
  };
  return (
    <Page
      title={getTranslatedText(intl, 'scheduling.event.template.title')}
      subtitle={getTranslatedText(
        intl,
        'scheduling.event.template.description'
      )}
      contentHeight="100px"
      primaryAction={
        <ButtonGroup>
          <Button onClick={onCancel} plainSecondary>
            <FormattedMessage id="common.cancel" />
          </Button>
          <Button
            primary
            onClick={onSave}
            disabled={loading}
            loading={updateLoading}
          >
            <FormattedMessage id="common.save" />
          </Button>
        </ButtonGroup>
      }
      variant="primary"
      // disableScroll={true}
    >
      {loading || metaLoading ? (
        <div className="height-60vh">
          <Loader />
        </div>
      ) : (
        <div className="flex g20 w100 event-template-editor ">
          <div className="height-60vh w100">
            <EmailEditor
              focusEditor
              editorRef={editorRef}
              template={template}
              updateTemplate={updateTemplate}
              handleFileAttachments={handleFileAttachments}
              handleAttachmentsDeletion={handleAttachmentsDeletion}
              title={getTranslatedText(intl, 'common.addAttachments')}
              description={getTranslatedText(
                intl,
                'common.upload.or.drop.files'
              )}
              disableAttachment
              placeholders={availablePlaceHolders}
              singleAttachment
              acceptAllType
              customToolbarComponent={CustomToolbar}
            />
          </div>

          <div className="height-60vh w50 rounded-2xl p20 scroll placeholder-container-background">
            <div className="mb-20">
              <Heading element="h2">
                <FormattedMessage id="admin.templates.placeholders.title" />
              </Heading>
            </div>

            <div>
              {availablePlaceHolders.map((el) => {
                return (
                  <div className="mb5" key={el?.key}>
                    #{el?.label}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </Page>
  );
};

export default EventTemplate;
